import styled from "styled-components";
import { Form, Input, Button } from "antd";
import { UseQueryResult, useMutation } from "@tanstack/react-query";
import {
  createDatabase,
  updateDatabase,
  openNotification,
  IDatabaseRequest,
  DatabaseType,
  IErrorResponse,
} from "@dataone/lib";
import SubHeader from "./SubHeader";
import { ErrorStep } from "../../steps/ErrorStep";
import { IDataSourceApp } from "../datasets-sources/DatasetsSources.config";
import { useState } from "react";
import { AxiosError } from "axios";

interface UpdateDatabaseFormProps {
  failed: boolean;
  databaseData: IDatabaseRequest | null;
  dataSource: IDataSourceApp;
  formMode: "update" | "create";
  setILoading: (submitClicked: boolean) => void;
  setCurrent: (current: number) => void;
  setFailed: (failed: boolean) => void;
  onCreateSuccess: () => void;
  refetchDatasets?: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const UpdateDatabaseForm = ({
  formMode,
  failed,
  databaseData,
  dataSource,
  setILoading,
  setCurrent,
  setFailed,
  onCreateSuccess,
  refetchDatasets,
}: UpdateDatabaseFormProps) => {
  const [databaseForm] = Form.useForm();
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { mutate: addDatabase, isLoading: isCreating } =
    useMutation(createDatabase);
  const { mutate: editDatabase, isLoading: isUpdating } =
    useMutation(updateDatabase);

  const handleOnSuccess = (message: string) => {
    // openNotification(message);
    setCurrent(2);
  };

  const handleSubmitForm = async (values: IDatabaseRequest) => {
    setILoading(true);
    formMode === "update"
      ? databaseData &&
        editDatabase(
          {
            ...values,
            type: dataSource.type as DatabaseType,
            id: databaseData.id,
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              refetchDatasets?.();
            },
            onError: (error) => {
              setILoading(false);
              setFailed(true);
              const { response } = error as AxiosError<IErrorResponse>;
              setErrorMessage(response?.data.message!);
            },
          }
        )
      : addDatabase(
          {
            ...values,
            type: dataSource.type as DatabaseType,
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              onCreateSuccess();
            },
            onError: (error) => {
              const { response } = error as AxiosError<IErrorResponse>;
              setILoading(false);
              setFailed(true);
              setErrorMessage(response?.data.message!);
            },
          }
        );
  };

  const rules = [
    {
      whitespace: true,
      required: true,
      message: "Please provide a value for this field.",
    },
  ];

  const numberRules = [
    { pattern: /[0-9]+/, message: "Please enter only numbers." },
    {
      required: true,
      message: "Please provide a value for this field.",
    },
  ];

  return (
    <>
      {failed ? (
        <ErrorStep errorMessage={errorMessage} />
      ) : (
        <>
          <StyledDatabaseForm $maxHeight="400px">
            <SubHeader icon={dataSource?.icon} label={dataSource.title} />
            <Form
              form={databaseForm}
              className="database-form"
              layout="vertical"
              onFinish={handleSubmitForm}
              {...(databaseData && { initialValues: databaseData })}
            >
              <Form.Item
                rules={rules}
                name="displayName"
                label={<StyledLabel>Display name</StyledLabel>}
              >
                <Input
                  placeholder="Display name"
                  className="input"
                  maxLength={255}
                />
              </Form.Item>
              <Form.Item
                rules={rules}
                name="name"
                label={<StyledLabel>Name</StyledLabel>}
              >
                <Input
                  className="input"
                  placeholder={
                    [DatabaseType.SAP, DatabaseType.SAGE].includes(
                      dataSource.type as DatabaseType
                    )
                      ? "Connector"
                      : "Database"
                  }
                  maxLength={255}
                />
              </Form.Item>
              <Form.Item
                rules={rules}
                name="host"
                label={<StyledLabel>Host</StyledLabel>}
              >
                <Input
                  className="input"
                  placeholder="127.0.0.1"
                  maxLength={255}
                />
              </Form.Item>
              <Form.Item
                rules={numberRules}
                name="port"
                label={<StyledLabel>Port</StyledLabel>}
              >
                <Input className="input" placeholder="3306" maxLength={255} />
              </Form.Item>
              <Form.Item
                rules={formMode === "create" ? rules : []}
                name="username"
                label={<StyledLabel>Username</StyledLabel>}
              >
                <Input
                  autoComplete="new-password"
                  placeholder="Username"
                  className="input"
                  maxLength={255}
                />
              </Form.Item>
              <Form.Item
                rules={formMode === "create" ? rules : []}
                name="password"
                label={<StyledLabel>Password</StyledLabel>}
              >
                <Input.Password
                  autoComplete="new-password"
                  placeholder="Password"
                  className="input"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </StyledDatabaseForm>
          <StyledFooterButtons isCreating={isCreating} isUpdating={isUpdating}>
            <Button
              className="submit-button"
              key="submit"
              type="primary"
              onClick={() => databaseForm.submit()}
            >
              {formMode === "update"
                ? isUpdating
                  ? "Updating ..."
                  : "Update"
                : isCreating
                ? "Creating ..."
                : "Create"}
            </Button>
          </StyledFooterButtons>
        </>
      )}
    </>
  );
};

const StyledDatabaseForm = styled.div<{ $maxHeight: string }>`
  .database-form {
    padding-left: 10%;
    padding-right: 10%;
  }
  .input {
    height: 40px;
    border-radius: 6px;
    background: #eff1fd !important;
    border: none;
    // background: #fff;
    box-shadow: 0px 4px 4px 0px #fff;
    font-size: 12px;
  }
  .ant-form-item-explain-error {
    font-size: 12px;
  }
  .ant-input:placeholder-shown {
    font-size: 12px;
  }
  max-height: ${(p) => p.$maxHeight};
  overflow-y: auto;
  margin-block: 35px;
  // width: 40vw;
  min-width: 450px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #8b8b8b;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: #fff;
    border: none;
  }

  &::-webkit-scrollbar-button:single-button:vertical:decrement {
    height: 12px;
    width: 16px;
    background-position: center 6px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='50,00 0,50 100,50'/></svg>");
  }
  &::-webkit-scrollbar-button:single-button:vertical:increment {
    height: 12px;
    width: 16px;
    background-position: center center;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='0,0 100,0 50,50'/></svg>");
  }
`;
const StyledFooterButtons = styled.div<{
  isCreating: boolean;
  isUpdating: boolean;
}>`
  .submit-button {
    position: absolute;
    right: 40px;
    bottom: 15px;
    font-size: 13px;
    width: 100px;
    cursor: ${({ isCreating, isUpdating }) =>
      isCreating || isUpdating ? "not-allowed" : "pointer"};
  }
`;
const StyledLabel = styled.span`
  color: rgba(4, 7, 38, 0.8);
  font-size: 13px;
  font-weight: 600;
`;

export default UpdateDatabaseForm;
